/**
 * Light / dark / system switch. Sits next to the language toggle in the top
 * bar, because "how it reads" and "what language it reads in" are the same kind
 * of decision. Dark is the product's default.
 */

import { Laptop, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu';
import { useI18n } from '@/contexts/I18nContext';
import { useTheme } from '@/contexts/ThemeContext';
import type { ThemeMode } from '@/lib/theme';
import { cn } from '@/lib/utils';

const OPTIONS: { mode: ThemeMode; labelKey: string; Icon: typeof Sun }[] = [
  { mode: 'light', labelKey: 'theme_light', Icon: Sun },
  { mode: 'dark', labelKey: 'theme_dark', Icon: Moon },
  { mode: 'system', labelKey: 'theme_system', Icon: Laptop },
];

export function ThemeToggle() {
  const { t, dir } = useI18n();
  const { theme, resolvedTheme, setTheme } = useTheme();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={t('theme')} title={t('theme')}>
          {resolvedTheme === 'dark' ? (
            <Moon className="size-4" />
          ) : (
            <Sun className="size-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'} className="min-w-40">
        {OPTIONS.map(({ mode, labelKey, Icon }) => (
          <DropdownMenuItem
            key={mode}
            onSelect={() => setTheme(mode)}
            className={cn(theme === mode && 'bg-accent text-accent-foreground')}
          >
            <Icon className="size-4" />
            {t(labelKey)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
